import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { trackEvent } from '../../../shared/utils/analytics';
import './FeedbackModal.css';

function FeedbackModal({ isOpen, onClose }) {
  const [feedback, setFeedback] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setFeedback('');
      setError('');
      setIsSubmitting(false);
      setIsSubmitted(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (event) => {
    event.preventDefault();
    const trimmedFeedback = feedback.trim();

    if (!trimmedFeedback) {
      setError('Please share a bit of feedback before sending.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch('/api/feedback.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ feedback: trimmedFeedback })
      });

      if (!response.ok) {
        const result = await response.json().catch(() => ({}));
        throw new Error(result.error || 'Failed to send feedback.');
      }

      trackEvent('feedback_submitted');
      setIsSubmitted(true);
    } catch (submitError) {
      console.error('Feedback submission failed:', submitError);
      setError(submitError.message || 'Failed to send feedback.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackdropClick = (event) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  const modalContent = (
    <div className="feedback-modal-backdrop" onClick={handleBackdropClick}>
      <div className="feedback-modal">
        <button className="feedback-modal-close text-non-selectable" onClick={onClose} aria-label="Close feedback form">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 4 L4 12 M4 4 L12 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        </button>

        <h2>Share feedback</h2>
        <p className="feedback-modal-subtitle">Tell us what is working or what could be better.</p>

        {isSubmitted ? (
          <div className="feedback-modal-success">
            Thanks for your feedback! We appreciate it.
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <label htmlFor="feedback-text" className="feedback-modal-label">Your feedback</label>
            <textarea
              id="feedback-text"
              value={feedback}
              onChange={(event) => setFeedback(event.target.value)}
              placeholder="Share your thoughts..."
              rows={5}
            />
            {error && <div className="feedback-modal-error">{error}</div>}
            <button type="submit" className="feedback-modal-submit text-non-selectable" disabled={isSubmitting}>
              {isSubmitting ? 'Sending...' : 'Send feedback'}
            </button>
          </form>
        )}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}

export default FeedbackModal;
